import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  OnInit,
  signal,
  ViewEncapsulation
} from '@angular/core';
import {
  DeclarationStatus,
  HarbourDuesDeclaration,
  StoreHarbourDuesDeclaration
} from "@portbase/bezoekschip-service-typescriptmodels";
import {HarbourDuesDeclarationTab} from "../types";
import {HarbourDuesService} from "../services/harbour-dues.service";
import {HarbourDuesFormService} from "../services/harbour-dues-form.service";
import {HarbourDuesStoreService} from "../services/harbour-dues-store.service";
import {HarbourDuesPermissionService} from "../services/harbour-dues-permission.service";
import {VisitContext} from "../../visit-context";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AppContext} from "../../../app-context";
import {openConfirmationModalWithCallback} from "../../../common/utils";
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from "../../../common/modal/modal-confirm.component";
import {sendCommandAndForget} from "../../../common/handlers/app-common-utils";

@Component({
  selector: 'app-harbour-dues-rotterdam',
  templateUrl: './harbour-dues-rotterdam.component.html',
  styleUrls: ['./harbour-dues-rotterdam.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class HarbourDuesRotterdamComponent implements OnInit {
  declarations: HarbourDuesDeclaration[] = [];
  protected readonly selectedDeclaration = signal<HarbourDuesDeclaration | undefined>(undefined);
  form = this.formService.createForm();
  showValidationErrors = false
  declarationTabs: HarbourDuesDeclarationTab[] = [];
  vesselDeadWeightKg?: number;

  protected readonly validProFormaDisplayed = signal(false);
  protected readonly isCorrection = computed (() => {
    return this.selectedDeclaration()?.status == "ACCEPTED" || this.selectedDeclaration()?.status == "REJECTED"
    }
  );

  protected readonly isDeclared = computed(() => {
    return this.selectedDeclaration()?.status == "DECLARED"
  })

  protected readonly hasCurrentVersionInvoice = computed (() => {
    const declaration = this.selectedDeclaration;
    return declaration()?.invoices?.some(
      i => i.statementVersion == declaration()?.statementVersion)
      || false
  })

  protected readonly saveAndSendDisabled = computed(() => {
    if (!AppContext.isHarbourDuesReviewer() && !VisitContext.isFinancialDeclarant()) {
      return true;
    } else if (!this.validProFormaDisplayed()) {
      return true;
    } else if (this.isCorrection()) {
      return !this.hasCurrentVersionInvoice();
    } else {
      return false;
    }
  });

  constructor(
    protected harbourDuesService: HarbourDuesService,
    protected formService: HarbourDuesFormService,
    protected storeService: HarbourDuesStoreService,
    protected permissionService: HarbourDuesPermissionService,
    protected destroyRef: DestroyRef) {
  }

  ngOnInit(): void {
    if (!this.permissionService.isAvailable()) {
      return;
    }

    this.harbourDuesService
      .getDeclarations(VisitContext.savedVisit.crn)
      .subscribe(declarations => {
        this.declarations = declarations;
        const portVisit = VisitContext?.savedVisit?.visitDeclaration?.portVisit;
        const ataPort = portVisit?.ataPort;
        const atdPort = portVisit?.atdPort;
        this.declarationTabs =
          this.harbourDuesService.declarationTabs(
            VisitContext.savedVisit.crn, ataPort, atdPort);
        this.selectDeclaration(this.declarationTabs[this.declarationTabs.length - 1]);
      });

    this.harbourDuesService.statusUpdates()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(update =>
        this.harbourDuesService.applyStatusUpdate(this.declarations, this.selectedDeclaration(), update));

    this.vesselDeadWeightKg = VisitContext.getVesselDeadWeightKg();
  }

  selectDeclaration(tab: HarbourDuesDeclarationTab) {
    this.selectedDeclaration.set(this.harbourDuesService.select(tab, this.declarations));
    this.selectedDeclaration.update(selectDeclaration => {
      if (!selectDeclaration) {
        return;
      }
      let updatedDeclaration = this.harbourDuesService.syncWithVisit(
        selectDeclaration, VisitContext.savedVisit
      );
      updatedDeclaration = this.harbourDuesService.syncWithPreviousDeclaration(
        updatedDeclaration, this.declarations
      );
      return updatedDeclaration;
    });

    this.formService.patchForm(this.form, this.selectedDeclaration());
  }

  saveDraft() {
    this.showValidationErrors = false;
    this.store(false);
  }

  declare() {
    if (!this.validate()) {
      return;
    }

    openConfirmationModalWithCallback(
      confirmed => confirmed && this.store(true),
      ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
        type: "primary",
        title: "Ready to send?",
        message: "Once sent, you won't be able to correct the declaration until it has been approved and a invoice has been sent.",
        messageSeperated: true,
        confirmText: "Send",
        cancelText: "Cancel",
        noBorder: true,
        alignHeaderBody: true
      },
      'static'
    );
  }

  rejectDeclaration() {
    openConfirmationModalWithCallback((confirmed, reason) => {
      if (confirmed) {
        sendCommandAndForget("com.portbase.bezoekschip.common.api.harbourdues.commands.RespondToHarbourDuesDeclaration", {
          crn: this.selectedDeclaration().crn,
          statementNumber: this.selectedDeclaration().statementNumber,
          accepted: false,
          rejectionReason: reason
        });
      }
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
      type: "primary",
      title: "Rejection reason",
      message: "Enter a short explanation on why the declaration was rejected.",
      withReasonTextArea: true,
      reasonLabel: "Type the reason here (required)",
      confirmText: "Reject & send",
      cancelText: "Go back"
    }, 'static');
  }

  acceptDeclaration() {
    sendCommandAndForget("com.portbase.bezoekschip.common.api.harbourdues.commands.RespondToHarbourDuesDeclaration", {
      crn: this.selectedDeclaration().crn,
      statementNumber: this.selectedDeclaration().statementNumber,
      accepted: true,
    });
  }

  validate(): boolean {
    this.showValidationErrors = this.form.invalid;
    if (this.form.invalid) {
      AppContext.registerError('Please review the fields with errors.');
    } else {
      AppContext.clearVisitAlerts();
    }
    return this.form.valid;
  }

  protected store(declare: boolean) {
    this.storeService
      .store(this.selectedDeclaration(), this.form.value, declare)
      .subscribe(command => {
        AppContext.registerSuccess(
          `Harbour dues declaration ${declare ? 'declared' : 'saved'} successfully`
        );
        this.patchDeclaration(command);
      });
  }

  protected patchDeclaration(command: StoreHarbourDuesDeclaration) {
    const {declare, discount, exemption, specialTariff, incidentals, visit} = command;
    this.selectedDeclaration.update(current => ({
      ...current!,
      status: declare ? DeclarationStatus.DECLARED : undefined,
      discount,
      exemption,
      specialTariff,
      incidentals,
      visit
    }));

  }

  get port() {
    return this.harbourDuesService.getPort();
  }

  declarationHasBerthVisits() {
    return this.declarationSelected()
      && this.selectedDeclaration()?.visit?.berthVisits
      && this.selectedDeclaration()?.visit?.berthVisits.length > 0;
  }

  declarationSelected(): boolean {
    return this.selectedDeclaration() != null;
  }

  get isAdmin() {
    return AppContext.isAdmin();
  }

  get isHarbourDuesAdvisor() {
    return AppContext.isHarbourDuesAdvisor();
  }

  get savedVisit() {
    return VisitContext.savedVisit;
  }
}
